/**
 * Turn confirmed app setup into engine inputs (worker-side).
 *
 * Microdata: indicator rules and collapse rules are applied as the
 * explicit table transformations automation provides (Q29), the
 * transformed table converts through @sae/io unchanged, and the
 * engine configuration is assembled from the confirmed roles and the
 * Step 2 run config. Area-level: the io area-level conversion with
 * the confirmed roles and the declared estimate scale.
 */

import type {
  CollapseRule,
  IndicatorRule,
  RoleMap,
} from '@sae/automation';
import { applyCollapses, recodeIndicators, subgroupLevelsOf } from '@sae/automation';
import type {
  AnalysisConfig,
  AnalysisInput,
  AreaCovariateRow,
  Issue,
  SubgroupSpec,
} from '@sae/engine';
import type { DataTable } from '@sae/io';
import { columnByName, normalizeAreaId, tableToRespondents } from '@sae/io';

import { disclosureConfigFor, resolveTransform } from '../domain/defaults.js';
import type { CovariateMode, RunConfigState } from '../domain/types.js';

export interface CompanionCovariates {
  readonly table: DataTable;
  readonly roles: RoleMap;
}

/**
 * Area covariate rows for a microdata run. A companion covariates
 * file, when loaded, supplies the covariates (its own detected
 * roles); otherwise the main file's covariate candidates are used —
 * detection only proposes columns that are constant within areas,
 * and the first value per area is taken (QC-14 warns when a
 * candidate actually varies). Population counts ride along when a
 * population-count column is present.
 */
export function microdataCovariateRows(
  table: DataTable,
  roles: RoleMap,
  companion: CompanionCovariates | null,
): { rows: AreaCovariateRow[]; populationCounts: Record<string, number> | null } {
  if (companion !== null) {
    const { table: ct, roles: cr } = companion;
    if (cr.areaId !== undefined) {
      const areaIdx = ct.columns.findIndex((c) => c.name === cr.areaId);
      const popIdx =
        cr.populationCount !== undefined
          ? ct.columns.findIndex((c) => c.name === cr.populationCount)
          : -1;
      const covIdx = cr.covariates.map((name) => ({
        name,
        idx: ct.columns.findIndex((c) => c.name === name),
      }));
      const rows: AreaCovariateRow[] = [];
      const counts: Record<string, number> = {};
      for (const row of ct.rows) {
        const areaCell = row[areaIdx];
        if (areaCell === null || areaCell === undefined || areaCell === '') {
          continue;
        }
        const areaId = normalizeAreaId(areaCell);
        const values: Record<string, number> = {};
        let complete = true;
        for (const { name, idx } of covIdx) {
          const v = idx >= 0 ? row[idx] : null;
          if (typeof v === 'number') values[name] = v;
          else complete = false;
        }
        if (!complete) continue;
        const pop = popIdx >= 0 ? row[popIdx] : null;
        if (typeof pop === 'number' && pop > 0) counts[areaId] = pop;
        rows.push({
          areaId,
          values,
          ...(typeof pop === 'number' && pop > 0
            ? { populationCount: pop }
            : {}),
        });
      }
      return {
        rows,
        populationCounts: Object.keys(counts).length > 0 ? counts : null,
      };
    }
  }

  const areaIdx = table.columns.findIndex((c) => c.name === roles.areaId);
  if (areaIdx < 0 || roles.covariates.length === 0) {
    return { rows: [], populationCounts: null };
  }
  const covIdx = roles.covariates.map((name) => ({
    name,
    idx: table.columns.findIndex((c) => c.name === name),
  }));
  const byArea = new Map<string, Record<string, number>>();
  for (const row of table.rows) {
    const areaCell = row[areaIdx];
    if (areaCell === null || areaCell === undefined || areaCell === '') {
      continue;
    }
    const areaId = normalizeAreaId(areaCell);
    let values = byArea.get(areaId);
    if (values === undefined) {
      values = {};
      byArea.set(areaId, values);
    }
    for (const { name, idx } of covIdx) {
      if (values[name] === undefined) {
        const v = idx >= 0 ? row[idx] : null;
        if (typeof v === 'number') values[name] = v;
      }
    }
  }
  const rows: AreaCovariateRow[] = [...byArea.entries()].map(
    ([areaId, values]) => ({ areaId, values }),
  );
  return { rows, populationCounts: null };
}

/** Subgroup specs for the engine config, from accepted collapses or raw levels. */
export function subgroupSpecsFor(
  workingTable: DataTable,
  sourceTable: DataTable,
  variables: readonly string[],
  collapses: Readonly<Record<string, CollapseRule>>,
): SubgroupSpec[] {
  return variables.map((variable) => {
    const rule = collapses[variable];
    if (rule !== undefined) {
      return {
        variable,
        levels: subgroupLevelsOf(rule).map((l) => ({
          value: l.value,
          label: l.label,
        })),
      };
    }
    const idx = workingTable.columns.findIndex((c) => c.name === variable);
    const labels = new Map<string | number, string>();
    const col = columnByName(sourceTable, variable);
    for (const vl of col?.valueLabels ?? []) labels.set(vl.value, vl.label);
    const seen: (string | number)[] = [];
    const seenSet = new Set<string | number>();
    if (idx >= 0) {
      for (const row of workingTable.rows) {
        const v = row[idx];
        if (v === null || v === undefined) continue;
        const key = v as string | number;
        if (!seenSet.has(key)) {
          seenSet.add(key);
          seen.push(key);
        }
      }
    }
    return {
      variable,
      levels: seen.map((value) => {
        const label = labels.get(value);
        return label !== undefined ? { value, label } : { value };
      }),
    };
  });
}

/** Population counts by normalized area id, from a roles-bearing table. */
export function extractPopulationCounts(
  table: DataTable,
  roles: RoleMap,
): Record<string, number> | null {
  if (roles.areaId === undefined || roles.populationCount === undefined) {
    return null;
  }
  const areaIdx = table.columns.findIndex((c) => c.name === roles.areaId);
  const popIdx = table.columns.findIndex(
    (c) => c.name === roles.populationCount,
  );
  if (areaIdx < 0 || popIdx < 0) return null;
  const counts: Record<string, number> = {};
  for (const row of table.rows) {
    const areaCell = row[areaIdx];
    const pop = row[popIdx];
    if (
      areaCell !== null &&
      areaCell !== undefined &&
      areaCell !== '' &&
      typeof pop === 'number' &&
      pop > 0
    ) {
      counts[normalizeAreaId(areaCell)] = pop;
    }
  }
  return Object.keys(counts).length > 0 ? counts : null;
}

/** Distinct normalized area ids of a table column, in file order. */
export function distinctAreaIds(table: DataTable, column: string): string[] {
  const idx = table.columns.findIndex((c) => c.name === column);
  if (idx < 0) return [];
  const seen = new Set<string>();
  for (const row of table.rows) {
    const v = row[idx];
    if (v !== null && v !== undefined && v !== '') {
      seen.add(normalizeAreaId(v));
    }
  }
  return [...seen];
}

export interface PreparedMicrodataRun {
  readonly input: AnalysisInput;
  readonly config: AnalysisConfig;
  /**
   * Transformation log entries as informational issues (brief
   * P3/P4), followed by the ingestion warnings of the conversion
   * (errors have already thrown above): the run's issue channel
   * carries them to the results screen.
   */
  readonly logIssues: readonly Issue[];
  readonly covariateNames: readonly string[];
}

/**
 * Apply rules and collapses, convert to respondents, and assemble
 * the full engine configuration for a microdata run. Throws an Error
 * with a plain-language message when conversion is impossible.
 */
export function prepareMicrodataRun(args: {
  table: DataTable;
  roles: RoleMap;
  indicatorRules: readonly IndicatorRule[];
  collapses: readonly CollapseRule[];
  subgroupsSelected: readonly string[];
  covariateModes: Readonly<Record<string, CovariateMode>>;
  companion: CompanionCovariates | null;
  runConfig: RunConfigState;
}): PreparedMicrodataRun {
  const { roles, runConfig } = args;
  if (roles.areaId === undefined || roles.weight === undefined) {
    throw new Error(
      'An area variable and a weight variable must be confirmed before the model can run.',
    );
  }
  if (args.indicatorRules.length === 0) {
    throw new Error('Select at least one indicator before running the model.');
  }

  const recoded = recodeIndicators(args.table, args.indicatorRules);
  let working = recoded.table;
  let collapseLog: typeof recoded.log = [];
  if (args.collapses.length > 0) {
    const collapsed = applyCollapses(working, args.collapses);
    working = collapsed.table;
    collapseLog = collapsed.log;
  }
  const logIssues: Issue[] = [...recoded.log, ...collapseLog].map((entry) => ({
    code: 'TRANSFORMATION',
    severity: 'info' as const,
    message: entry.note,
  }));

  const conversion = tableToRespondents(working, {
    area: roles.areaId,
    weight: roles.weight,
    ...(roles.stratum !== undefined ? { stratum: roles.stratum } : {}),
    ...(roles.psu !== undefined ? { psu: roles.psu } : {}),
    ...(roles.psuPopulationCount !== undefined
      ? { psuPopulation: roles.psuPopulationCount }
      : {}),
    indicators: args.indicatorRules.map((r) => r.sourceColumn),
    subgroups: args.subgroupsSelected,
  });
  const errors = conversion.issues.filter((i) => i.severity === 'error');
  if (errors.length > 0) {
    throw new Error(
      `The file could not be converted for analysis: ${errors[0].message}`,
    );
  }
  if (conversion.respondents.length === 0) {
    throw new Error('No usable respondents remain after conversion.');
  }
  // The conversion's warnings ride the run's issue channel: a
  // partial or empty frame column (Addendum 37), missing weights,
  // and their kin are findings of this run, not silent adjustments.
  logIssues.push(
    ...conversion.issues
      .filter((i) => i.severity === 'warning')
      .map((i) => ({
        code: i.code,
        severity: 'warning' as const,
        message: i.message,
      })),
  );

  const { rows: covariateRows, populationCounts } = microdataCovariateRows(
    working,
    roles,
    args.companion,
  );
  const candidateNames =
    args.companion !== null && args.companion.roles.covariates.length > 0
      ? args.companion.roles.covariates
      : roles.covariates;
  const modeOf = (name: string): CovariateMode =>
    args.covariateModes[name] ?? 'auto';
  const candidates = candidateNames.filter((n) => modeOf(n) !== 'excluded');
  const locked = candidateNames.filter((n) => modeOf(n) === 'locked');

  const collapseMap: Record<string, CollapseRule> = {};
  for (const c of args.collapses) collapseMap[c.variable] = c;

  const config: AnalysisConfig = {
    design: {
      weight: roles.weight,
      ...(roles.stratum !== undefined ? { stratum: roles.stratum } : {}),
      ...(roles.psu !== undefined ? { psu: roles.psu } : {}),
      lonelyPsu: 'adjust',
      fpc: false,
      // Frame PSU counts collected from the upload's frame column
      // (Addendum 37): when the file carries them, the corrected
      // P2 basis is the run's basis; absent, the design config is
      // exactly the shipped one and behavior is byte-unchanged.
      ...(conversion.psuPopulationCounts !== undefined
        ? { psuPopulationCounts: conversion.psuPopulationCounts }
        : {}),
    },
    area: {
      id: roles.areaId,
      ...(roles.areaName !== undefined ? { name: roles.areaName } : {}),
      // Out-of-sample estimates stay off (decision O3, ruling S4).
      outOfSample: false,
    },
    indicators: args.indicatorRules.map((rule) => ({
      id: rule.id,
      ...(rule.label !== undefined ? { label: rule.label } : {}),
      // The recoded table holds 0/1; 1 is the event (brief AUT-03).
      yesValues: [1],
    })),
    subgroups: subgroupSpecsFor(
      working,
      args.table,
      args.subgroupsSelected,
      collapseMap,
    ),
    covariates: {
      candidates,
      locked,
      excluded: candidateNames.filter((n) => modeOf(n) === 'excluded'),
      selection: {
        criterion: 'bic',
        scope: 'overall',
        correlationThreshold: 0.9,
        vifThreshold: 10,
      },
    },
    model: {
      transform: resolveTransform(runConfig.transform),
      backTransform: 'bc',
      transformBiasCorrection: runConfig.transformBiasCorrection === true,
      deff: {
        method: runConfig.deffBasis === 'pooled' ? 'pooled' : 'state',
        min: 1,
        max: 5,
      },
      // Shipped minimum-areas default (decision S4, directive C-7):
      // fit cells with 5 to 9 areas under the LOW_AREA_COUNT warning;
      // refuse below 5.
      minAreas: 5,
    },
    uncertainty: {
      mode: runConfig.mode,
      bootstrapB: runConfig.bootstrapB,
      seed: runConfig.seed,
      confidence: runConfig.confidence,
    },
    benchmarking: {
      external: runConfig.benchmarkingEnabled
        ? runConfig.benchmarkingMethod
        : 'none',
      internal: false,
    },
    // Estimate policy A: the model estimate is reported (brief 9.11).
    policy: { type: 'model' },
    disclosure: disclosureConfigFor(runConfig.disclosurePresetId),
  };

  const input: AnalysisInput = {
    respondents: conversion.respondents,
    ...(covariateRows.length > 0 ? { areaCovariates: covariateRows } : {}),
    ...(populationCounts !== null ? { populationCounts } : {}),
  };
  return { input, config, logIssues, covariateNames: candidates };
}
